import { useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, matchPath, useLocation } from 'react-router';

import { getHomePath } from '@/entities/session';
import { useAppSelector } from '@/shared/model/storeHooks';

interface PrivateRouteProps {
  access: (auth: RootState['auth']) => boolean;
  children: ReactNode;
}

interface GuestRouteProps {
  children: ReactNode;
}

export const PrivateRoute = ({ access, children }: PrivateRouteProps) => {
  const auth = useAppSelector((store) => store.auth);
  const location = useLocation();

  return access(auth) ? (
    children
  ) : (
    <Navigate to={getHomePath(auth)} replace state={{ from: location }} />
  );
};

// Checked once per visit, not on every auth change: guest pages sign the user in
// and then pick the destination themselves (e.g. the welcome screen after login).
// Render it with a `key` per route so the check is repeated on each navigation.
export const GuestRoute = ({ children }: GuestRouteProps) => {
  const auth = useAppSelector((store) => store.auth);
  const [redirectPath] = useState(() =>
    auth.accessToken ? getHomePath(auth) : null,
  );

  return redirectPath ? <Navigate to={redirectPath} replace /> : children;
};

interface GuardedLayoutProps {
  routes: { path: string; access?: (auth: RootState['auth']) => boolean }[];
  children: ReactNode;
}

// Checks the access rule of the page before its layout is rendered: a layout route renders
// its frame (header, sidebar) first and the page's own PrivateRoute only inside it, so a
// signed-out visitor would see the signed-in frame before the redirect (AU-A-03).
export const GuardedLayout = ({ routes, children }: GuardedLayoutProps) => {
  const auth = useAppSelector((store) => store.auth);
  const location = useLocation();
  const route = routes.find(({ path }) => matchPath(path, location.pathname));

  if (route?.access && !route.access(auth)) {
    return (
      <Navigate to={getHomePath(auth)} replace state={{ from: location }} />
    );
  }

  return children;
};

export const HomeRedirect = () => {
  const auth = useAppSelector((store) => store.auth);

  return <Navigate to={getHomePath(auth)} replace />;
};
